import React, { useEffect, useMemo, useState } from "react";
import {
  createTenant,
  fetchTenant,
  fetchThemeTemplates,
  generateTenantAi,
  resolveTenant,
  updateTenantAiProfile,
  updateTenantBranding,
  updateTenantModules,
} from "../api/adminApi";

const DEFAULT_FORM = {
  tenant_id: "",
  company_name: "",
  domain: "",
  industry_type: "general",
  company_description: "",
  main_business_areas: "",
  departments: "",
  organization_chart: "",
  app_display_name: "",
  ai_display_name: "",
  theme_id: "executive-clean",
  primary_color: "#2563eb",
  accent_color: "#ff7a00",
  logo_url: "",
  welcome_message: "",
  ai_role_description: "",
  ai_speaking_style: "professional",
  allowed_tasks: "문서 요약, 일정 정리, 메일 초안, 회의록 작성, 회사 지식 검색",
  restricted_tasks: "결제 실행, DNS 변경, 서버 배포, 외부 발송, 개인정보 다운로드",
  modules: {
    dashboard: true,
    documents: true,
    talk: true,
    schedule: true,
    mail: true,
    codex: false,
    admin: false,
  },
};

const MODULE_LABELS = {
  dashboard: "업무 홈",
  documents: "문서함",
  talk: "톡/회의",
  schedule: "일정",
  mail: "메일",
  codex: "TOBE Codex",
  admin: "관리자",
};

function splitList(value) {
  return String(value || "")
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function safeTenantId(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50);
}

function buildDefaultDomain(tenantId) {
  return tenantId ? `${tenantId}.to-be.kr` : "";
}

function TenantOnboardingPanel({ apiBase, token }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [themes, setThemes] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedTheme = useMemo(
    () => themes.find((theme) => theme.theme_id === form.theme_id),
    [themes, form.theme_id],
  );

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateCompanyName = (value) => {
    setForm((prev) => {
      const next = { ...prev, company_name: value };
      if (!next.app_display_name) next.app_display_name = `${value} AI`;
      if (!next.ai_display_name) next.ai_display_name = `${value} 전용 AI`;
      return next;
    });
  };

  const updateTenantId = (value) => {
    const tenantId = safeTenantId(value);
    setForm((prev) => ({
      ...prev,
      tenant_id: tenantId,
      domain: prev.domain && prev.domain !== buildDefaultDomain(prev.tenant_id) ? prev.domain : buildDefaultDomain(tenantId),
    }));
  };

  const updateModule = (field) => {
    setForm((prev) => ({
      ...prev,
      modules: { ...prev.modules, [field]: !prev.modules[field] },
    }));
  };

  const loadThemes = async () => {
    try {
      const data = await fetchThemeTemplates(apiBase, token);
      setThemes(data.items || []);
      if (!form.theme_id && data.items?.[0]?.theme_id) {
        updateField("theme_id", data.items[0].theme_id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const buildPayload = () => {
    const tenantId = safeTenantId(form.tenant_id);
    const roleDescription = [
      form.ai_role_description || `${form.company_name}의 전용 회사 AI입니다.`,
      form.company_description ? `회사 소개: ${form.company_description}` : "",
      form.main_business_areas ? `주요 업무: ${form.main_business_areas}` : "",
      form.departments ? `조직/부서: ${form.departments}` : "",
      form.organization_chart ? `조직도/책임 구조: ${form.organization_chart}` : "",
    ].filter(Boolean).join("\n");

    return {
      tenant_id: tenantId,
      company_name: form.company_name.trim(),
      domain: (form.domain.trim() || buildDefaultDomain(tenantId)),
      industry_type: form.industry_type.trim() || "general",
      company_description: form.company_description.trim(),
      main_business_areas: splitList(form.main_business_areas),
      departments: splitList(form.departments),
      branding: {
        app_display_name: form.app_display_name.trim() || `${form.company_name} AI`,
        ai_display_name: form.ai_display_name.trim() || `${form.company_name} 전용 AI`,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
        theme_id: form.theme_id,
        logo_url: form.logo_url.trim(),
        welcome_message: form.welcome_message.trim(),
      },
      ai_profile: {
        ai_display_name: form.ai_display_name.trim() || `${form.company_name} 전용 AI`,
        ai_role_description: roleDescription,
        ai_speaking_style: form.ai_speaking_style,
        allowed_tasks: splitList(form.allowed_tasks),
        restricted_tasks: splitList(form.restricted_tasks),
        default_language: "ko",
        memory_scope: "tenant-only",
      },
      modules: form.modules,
    };
  };

  const validatePayload = (payload) => {
    if (!payload.tenant_id) throw new Error("회사 ID를 입력해주세요. 영문, 숫자, 하이픈만 사용할 수 있습니다.");
    if (!payload.company_name) throw new Error("회사명을 입력해주세요.");
    if (!payload.domain) throw new Error("접속 도메인을 입력해주세요.");
    if (!payload.branding.app_display_name) throw new Error("앱 표시 이름을 입력해주세요.");
    if (!payload.ai_profile.ai_display_name) throw new Error("AI 표시 이름을 입력해주세요.");
  };

  const handleCreate = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = buildPayload();
      validatePayload(payload);
      const created = await createTenant(apiBase, token, payload);
      const tenantId = created.tenant_id || payload.tenant_id;
      await updateTenantBranding(apiBase, token, tenantId, payload.branding);
      await updateTenantAiProfile(apiBase, token, tenantId, payload.ai_profile);
      await updateTenantModules(apiBase, token, tenantId, { modules: payload.modules });
      await generateTenantAi(apiBase, token, tenantId);
      const latest = await fetchTenant(apiBase, token, tenantId);
      const hostCheck = await resolveTenant(apiBase, payload.domain);
      setTenant(latest);
      setResolved(hostCheck);
      setNotice("회사 전용 AI 설정을 저장했습니다. 도메인 연결과 앱 표시 정보가 갱신됩니다.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleResolve = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await resolveTenant(apiBase, form.domain || buildDefaultDomain(form.tenant_id));
      setResolved(data);
      setNotice("도메인 연결 상태를 확인했습니다.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="dashboard-panel">
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">COMPANY AI</p>
          <h3>회사 전용 AI 생성</h3>
          <p>회사 정보, 브랜드, 템플릿, AI 역할을 입력하면 기업별 전용 TOBE AI 작업공간을 구성합니다.</p>
        </div>
        <button type="button" className="admin-approve-button" onClick={handleCreate} disabled={busy}>
          {busy ? "처리 중" : "회사 AI 저장"}
        </button>
      </div>

      {notice && <div className="admin-inline-notice">{notice}</div>}
      {error && <div className="admin-error">{error}</div>}

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <p className="panel-kicker">BASIC</p>
        <h3>회사 기본 정보</h3>
        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
          <label>
            회사 ID
            <input value={form.tenant_id} onChange={(event) => updateTenantId(event.target.value)} placeholder="예: esai 또는 tobe-demo" />
          </label>
          <label>
            접속 도메인
            <input value={form.domain} onChange={(event) => updateField("domain", event.target.value)} placeholder="예: esai.to-be.kr" />
          </label>
          <label>
            회사명
            <input value={form.company_name} onChange={(event) => updateCompanyName(event.target.value)} placeholder="예: 주식회사 투비" />
          </label>
          <label>
            업종
            <input value={form.industry_type} onChange={(event) => updateField("industry_type", event.target.value)} placeholder="예: marine, sales, manufacturing" />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            회사 소개
            <textarea value={form.company_description} onChange={(event) => updateField("company_description", event.target.value)} rows={4} placeholder="회사 소개, 주요 고객, 핵심 가치, 운영 방식 등을 입력하세요." />
          </label>
          <label>
            주요 업무
            <textarea value={form.main_business_areas} onChange={(event) => updateField("main_business_areas", event.target.value)} rows={3} placeholder="쉼표 또는 줄바꿈으로 입력" />
          </label>
          <label>
            조직/부서
            <textarea value={form.departments} onChange={(event) => updateField("departments", event.target.value)} rows={3} placeholder="예: 경영지원, 영업, 운영, 개발" />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            조직도/책임 구조
            <textarea value={form.organization_chart} onChange={(event) => updateField("organization_chart", event.target.value)} rows={3} placeholder="대표, 팀장, 담당자, 승인 흐름 등 AI가 알아야 할 조직 구조를 입력하세요." />
          </label>
        </div>
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <p className="panel-kicker">BRAND</p>
        <h3>브랜드와 템플릿</h3>
        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
          <label>
            앱 표시 이름
            <input value={form.app_display_name} onChange={(event) => updateField("app_display_name", event.target.value)} placeholder="예: ES AI" />
          </label>
          <label>
            AI 표시 이름
            <input value={form.ai_display_name} onChange={(event) => updateField("ai_display_name", event.target.value)} placeholder="예: ES 전용 AI" />
          </label>
          <label>
            템플릿
            <select value={form.theme_id} onChange={(event) => updateField("theme_id", event.target.value)}>
              {themes.map((theme) => (
                <option key={theme.theme_id} value={theme.theme_id}>
                  {theme.theme_name} / {theme.industry_type}
                </option>
              ))}
            </select>
          </label>
          <label>
            로고 URL
            <input value={form.logo_url} onChange={(event) => updateField("logo_url", event.target.value)} placeholder="https://..." />
          </label>
          <label>
            대표 색상
            <input value={form.primary_color} onChange={(event) => updateField("primary_color", event.target.value)} placeholder="#2563eb" />
          </label>
          <label>
            강조 색상
            <input value={form.accent_color} onChange={(event) => updateField("accent_color", event.target.value)} placeholder="#ff7a00" />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            첫 인사
            <textarea value={form.welcome_message} onChange={(event) => updateField("welcome_message", event.target.value)} rows={3} placeholder="사용자가 접속했을 때 회사 AI가 어떤 톤으로 안내할지 입력하세요." />
          </label>
        </div>
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <p className="panel-kicker">AI POLICY</p>
        <h3>AI 역할과 작업 범위</h3>
        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
          <label>
            말투
            <select value={form.ai_speaking_style} onChange={(event) => updateField("ai_speaking_style", event.target.value)}>
              <option value="professional">전문적이고 간결하게</option>
              <option value="friendly">친근하고 부드럽게</option>
              <option value="executive">임원 보고형</option>
              <option value="field">현장 작업형</option>
            </select>
          </label>
          <label>
            도메인 확인
            <button type="button" className="admin-secondary-button" onClick={handleResolve} disabled={busy}>
              도메인 연결 확인
            </button>
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            AI 역할 설명
            <textarea value={form.ai_role_description} onChange={(event) => updateField("ai_role_description", event.target.value)} rows={4} placeholder="이 회사 AI가 어떤 업무를 도와야 하는지, 어떤 기준으로 답해야 하는지 입력하세요." />
          </label>
          <label>
            허용 작업
            <textarea value={form.allowed_tasks} onChange={(event) => updateField("allowed_tasks", event.target.value)} rows={4} />
          </label>
          <label>
            승인 필요/제한 작업
            <textarea value={form.restricted_tasks} onChange={(event) => updateField("restricted_tasks", event.target.value)} rows={4} />
          </label>
        </div>
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <div className="panel-heading">
          <div>
            <p className="panel-kicker">MODULES</p>
            <h3>사용 모듈</h3>
          </div>
        </div>
        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
          {Object.keys(form.modules).map((key) => (
            <label key={key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={form.modules[key]} onChange={() => updateModule(key)} />
              {MODULE_LABELS[key] || key}
            </label>
          ))}
        </div>
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <p className="panel-kicker">RESULT</p>
        <h3>{tenant?.company_name || form.company_name || "회사 AI 미리보기"}</h3>
        <p>템플릿: {selectedTheme?.theme_name || form.theme_id}</p>
        <p>도메인: {resolved?.domain || form.domain || "-"}</p>
        <p>앱: {tenant?.branding?.app_display_name || form.app_display_name || "-"}</p>
        <p>AI: {tenant?.ai_profile?.ai_display_name || form.ai_display_name || "-"}</p>
        <p>상태: {resolved?.status || tenant?.status || "확인 전"}</p>
      </div>
    </section>
  );
}

export default TenantOnboardingPanel;
